// ./src/components/features/Refuels/TankLevelSlider/TankLevelSlider.tsx
import React, { useMemo, useCallback } from "react";
import { Box, Slider, Group, Text, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { useUserProfileContext } from "@/contexts";

// ============================================
// Types
// ============================================

export interface TankLevelSliderProps {
  /** Field name for form binding */
  name?: string;

  /** Current tank level percentage (0-100 for UI slider) */
  value?: number;

  /** Callback fired when tank level changes: (name, value) */
  onChange?: (name: string, value: number) => void;

  /** Tank volume from selected car (in user's volume units) */
  tankVolume?: number;

  /** Volume of fuel added during this refuel */
  refuelVolume?: number;

  /** Whether the slider is disabled */
  disabled?: boolean;

  /** Size of the slider */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;

  /** Whether to show the calculated values */
  showCalculations?: boolean;

  /** Error message to display */
  error?: string | null;
}

export interface TankCalculations {
  /** Current fuel in tank as percentage (0.0-1.0, where 1.0 = 100%) */
  fuelInTank: number;
  /** Current fuel in tank in volume units (for display) */
  fuelInTankVolume: number;
  /** Whether the tank is full (100%) */
  isFullTank: boolean;
  /** Estimated fuel remaining before refuel (volume units, for display) */
  remainingInTankBeforeVolume: number;
  /** Estimated fuel remaining before refuel as percentage (0.0-1.0) */
  remainingInTankBefore: number;
  /** Tank level percentage (0-100, for UI display) */
  tankPercentage: number;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Calculate tank values based on percentage, tank volume, and refuel volume
 * @param tankPercentage - Tank level as 0-100 (UI slider value)
 * @param tankVolume - Tank capacity in volume units
 * @param refuelVolume - Volume of fuel added during this refuel
 */
export const calculateTankValues = (
  tankPercentage: number,
  tankVolume: number,
  refuelVolume: number
): TankCalculations => {
  // Convert percentage (0-100) to decimal (0.0-1.0)
  const fuelInTank = tankPercentage / 100;

  // Calculate volume for display purposes
  const fuelInTankVolume = fuelInTank * tankVolume;

  const isFullTank = tankPercentage >= 100;

  // Calculate remaining before refuel (in volume)
  const remainingInTankBeforeVolume = Math.max(
    0,
    fuelInTankVolume - refuelVolume
  );

  // Calculate remaining before refuel as percentage (0.0-1.0)
  const remainingInTankBefore =
    tankVolume > 0 ? Math.max(0, remainingInTankBeforeVolume / tankVolume) : 0;

  return {
    fuelInTank: Math.round(fuelInTank * 100) / 100, // Round to 2 decimals (e.g., 0.75)
    fuelInTankVolume: Math.round(fuelInTankVolume * 10) / 10, // Round to 1 decimal
    isFullTank,
    remainingInTankBeforeVolume:
      Math.round(remainingInTankBeforeVolume * 10) / 10,
    remainingInTankBefore: Math.round(remainingInTankBefore * 100) / 100,
    tankPercentage,
  };
};

// ============================================
// Component
// ============================================

const TankLevelSliderComponent: React.FC<TankLevelSliderProps> = ({
  name = "tankLevel",
  value = 50,
  onChange,
  tankVolume = 0,
  refuelVolume = 0,
  disabled = false,
  size = "md",
  label,
  showCalculations = true,
  error,
}) => {
  const { t } = useTranslation(["refuels", "common"]);
  const { volumeLabelShort } = useUserProfileContext();

  // Default label
  const fieldLabel = label ?? t("refuels:form.fields.tankLevel.label");

  // Calculate tank values
  const calculations = useMemo(
    () => calculateTankValues(value, tankVolume, refuelVolume),
    [value, tankVolume, refuelVolume]
  );

  // Handle slider change
  const handleChange = useCallback(
    (newValue: number) => {
      onChange?.(name, newValue);
    },
    [name, onChange]
  );

  // Slider marks for E and F labels
  const marks = useMemo(
    () => [
      { value: 0, label: t("refuels:form.fields.tankLevel.empty") },
      { value: 25 },
      { value: 50 },
      { value: 75 },
      { value: 100, label: t("refuels:form.fields.tankLevel.full") },
    ],
    [t]
  );

  // Format percentage for display (include volume if tank volume is known)
  const percentageDisplay = useMemo(() => {
    const percentText = t("refuels:form.fields.tankLevel.percentage", {
      value,
    });
    if (tankVolume > 0) {
      return `${percentText} (${calculations.fuelInTankVolume} ${t(`common:${volumeLabelShort}`)})`;
    }
    return percentText;
  }, [t, value, tankVolume, calculations.fuelInTankVolume, volumeLabelShort]);

  // Determine slider color based on level
  const sliderColor = useMemo(() => {
    if (value <= 15) return "red";
    if (value <= 30) return "orange";
    if (value <= 50) return "yellow";
    return "green";
  }, [value]);

  return (
    <Box>
      {/* Label and percentage */}
      <Group justify="space-between" mb="xs">
        <Text size="sm" fw={500}>
          {fieldLabel}
        </Text>
        <Text size="sm" fw={600} c={sliderColor}>
          {percentageDisplay}
        </Text>
      </Group>

      {/* Slider */}
      <Slider
        value={value}
        onChange={handleChange}
        min={0}
        max={100}
        step={5}
        marks={marks}
        size={size}
        color={sliderColor}
        disabled={disabled}
        label={(val) => `${val}%`}
        styles={{
          root: {
            paddingTop: 4,
            paddingBottom: 24,
          },
          markLabel: {
            fontSize: "var(--mantine-font-size-sm)",
            fontWeight: 600,
          },
        }}
      />

      {/* Calculated values */}
      {showCalculations && tankVolume > 0 && (
        <Stack gap={4} mt="xs">
          {refuelVolume > 0 && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {t("refuels:form.fields.tankLevel.beforeRefuel", {
                  defaultValue: "Before refuel",
                })}
              </Text>
              <Text size="xs" fw={500}>
                ~{calculations.remainingInTankBeforeVolume} {t(`common:${volumeLabelShort}`)} (
                {Math.round(calculations.remainingInTankBefore * 100)}%)
              </Text>
            </Group>
          )}

          {calculations.isFullTank && (
            <Text size="xs" c="green" fw={500}>
              ✓ {t("refuels:form.fields.isFullTank.label")}
            </Text>
          )}
        </Stack>
      )}

      {/* Error message */}
      {error && (
        <Text size="xs" c="red" mt="xs">
          {error}
        </Text>
      )}
    </Box>
  );
};

export const TankLevelSlider = React.memo(TankLevelSliderComponent);
